import type { FormEvent } from 'react';
import type React from 'react';
import { useState } from 'react';
import { useIsFetching, useIsMutating } from '@zg-rentals/react-query5';

import { Button, ButtonGroup, Heading, TextButton } from '@zillow/constellation-10';
import { useRentalManagerUser } from '@zg-rentals/rental-manager-env';

import ShareLeaseContent from './RequestSignatureModal/ShareLeaseContent';
import EmailPreviewContent from './RequestSignatureModal/EmailPreviewContent';
import LeaseSentContent from './RequestSignatureModal/LeaseSentContent';
import LeaseModal from './Containers/LeaseModal';
import type { Property } from '../models';
import { Lease } from '../models';
import { LeaseArtifactType } from '@zg-rentals/ts-leasing-client';
import {
  useAttachedLeaseAcceptanceForUserQuery,
  useDisclaimerAcceptanceForUserQuery,
  useLandlordProvidedDocumentsQuery,
  useSendForOfflineSigningMutation,
  useSendForOnlineSigningMutation,
  useSetAttachedLeaseAcceptanceForUserMutation,
  useSetDisclaimerAcceptanceForUserMutation,
  useWebAnalyticsForListingQuery,
} from '../utils/queries';
import type { LandlordProvidedDocument, RouterHistory } from '../models/Types';
import { LeaseTypes, TabIds } from '../models/Types';
import { useLeasingUrlParams } from '../hooks/useLeasingUrlParams';
import DownloadButton from './Widgets/DownloadButton';
import * as constants from '../components/RequestSignatureModal/constants';
import { CANCEL } from '../constants/stringConstants';
import { emitRequestElectronicSignaturesEvent } from '../../analytics/events';
import { emitRequestSignatureWizardEvent } from '../../analytics/events';

type Props = {
  lease: Lease;
  history: RouterHistory;
  property: Property;
  onClose(): void;
};

export function RequestSignatureModal({ lease, history, onClose, property }: Props): JSX.Element {
  const { user } = useRentalManagerUser();
  const { propertyId } = useLeasingUrlParams();
  const [activeComponent, setActiveComponent] = useState(RequestSignatureModal.ShareLeaseContent);
  const [isInSignOnlineMode, setIsInSignOnlineMode] = useState(true);
  const [personalNote, setStatePersonalNote] = useState(null);
  const [acceptedDisclaimerChecked, setAcceptedDisclaimerChecked] = useState(false);
  const [transitioning, setTransitioning] = useState(false);
  const [acceptedLeaseAttachmentChecked, setAcceptedLeaseAttachmentChecked] = useState(false);

  // React Query hooks
  const webAnalyticsQuery = useWebAnalyticsForListingQuery(propertyId, { enabled: !!propertyId });
  const zrm_listing_info = webAnalyticsQuery.data;

  const { data: landlordProvidedDocumentsData } = useLandlordProvidedDocumentsQuery(lease.id);
  const landlordProvidedDocuments =
    landlordProvidedDocumentsData?.documentNames?.map((name) =>
      typeof name === 'string' ? { text: name } : (name as LandlordProvidedDocument),
    ) || [];

  const { data: disclaimerData } = useDisclaimerAcceptanceForUserQuery(lease.id);
  const acceptedDisclaimer = disclaimerData?.acceptedDisclaimer ?? false;

  const { data: attachedLeaseData } = useAttachedLeaseAcceptanceForUserQuery(lease.id);
  const acceptedAttachedLease = attachedLeaseData?.acceptedAttachedLease ?? false;

  const sendForOnlineSigningMutation = useSendForOnlineSigningMutation();
  const sendForOfflineSigningMutation = useSendForOfflineSigningMutation();
  const setDisclaimerMutation = useSetDisclaimerAcceptanceForUserMutation();
  const setAttachedLeaseMutation = useSetAttachedLeaseAcceptanceForUserMutation();

  const isFetching = useIsFetching() > 0;
  const isMutating = useIsMutating() > 0;

  const stateName = property?.getStateName() ?? '';
  const isSelfUpload = lease.isSelfUpload;

  function gotoDashboard(): void {
    onClose();
    history.push(Lease.getDashboardPath());
  }

  function onOpenEmailPreview(): void {
    setActiveComponent(RequestSignatureModal.EmailPreviewContent);
  }

  function onCloseEmailPreview(): void {
    setActiveComponent(RequestSignatureModal.ShareLeaseContent);
  }

  function onModalCloseWithTracking(): void {
    onClose();
  }

  function onSigningTabSelected(id: TabIds): void {
    if (id === TabIds.ONLINE_SIGNING) {
      setIsInSignOnlineMode(true);
    } else if (id === TabIds.PRINT_TO_SIGN) {
      setIsInSignOnlineMode(false);
    }
  }

  async function onSendClick(message: string): Promise<void> {
    if (isInSignOnlineMode) {
      await sendForOnlineSigningMutation.mutateAsync({
        postData: {
          leaseArtifactType: LeaseArtifactType.LEASE,
          leaseArtifactId: lease.id as unknown as number,
          message: message || '',
        },
      });
    } else {
      await sendForOfflineSigningMutation.mutateAsync({
        postData: {
          leaseId: lease.id as unknown as number,
          message: message || '',
        },
      });
    }

    setActiveComponent(RequestSignatureModal.LeaseSentContent);
  }

  function onPersonalNoteChange(event: FormEvent<HTMLTextAreaElement>) {
    const { value } = event.currentTarget;

    setStatePersonalNote(value);
  }

  function setPersonalNote(note: string) {
    setStatePersonalNote(note);
  }

  function goToDashboard(): void {
    gotoDashboard();
  }

  function onCancelClick(): void {
    onClose();
  }

  async function sendClick(): Promise<void> {
    setTransitioning(true);
    await onSendClick(personalNote);
    setTransitioning(false);

    if (acceptedDisclaimerChecked) {
      setDisclaimerMutation.mutate({ postData: { leaseId: lease.id as unknown as number } });
    }
    if (acceptedLeaseAttachmentChecked) {
      setAttachedLeaseMutation.mutate({ postData: { leaseId: lease.id as unknown as number } });
    }
    const leaseWithNumericId = { ...lease, id: Number(lease.id) };
    if (lease.leaseType === LeaseTypes.ZILLOW_WIZARD) {
      emitRequestSignatureWizardEvent({ user, listing: zrm_listing_info, lease: leaseWithNumericId });
    } else if (lease.leaseType === LeaseTypes.SELF_UPLOAD || lease.leaseType === LeaseTypes.SIGNED_SELF_UPLOAD) {
      emitRequestElectronicSignaturesEvent({ user, listing: zrm_listing_info, lease: leaseWithNumericId });
    }
  }

  function renderFooter(): React.ReactNode {
    const sentContent = isSelfUpload
      ? constants.SELF_UPLOAD.LEASE_SENT_CONTENT
      : isInSignOnlineMode
        ? constants.SIGN_ONLINE.LEASE_SENT_CONTENT
        : constants.PRINT_TO_SIGN.LEASE_SENT_CONTENT;

    const shareContent = isSelfUpload
      ? constants.SELF_UPLOAD.SHARE_LEASE_CONTENT
      : isInSignOnlineMode
        ? constants.SIGN_ONLINE.SHARE_LEASE_CONTENT
        : constants.PRINT_TO_SIGN.SHARE_LEASE_CONTENT;

    const footerMapping = {
      [RequestSignatureModal.ShareLeaseContent]: (
        <ButtonGroup aria-label="send for signatures">
          <TextButton tone="brand" onClick={onCancelClick} disabled={transitioning}>
            {CANCEL}
          </TextButton>
          <Button
            tone="brand"
            emphasis="filled"
            className="primary-button"
            onClick={sendClick}
            // acceptedDisclaimerChecked is the state of the checkbox.
            // acceptedDisclaimerBefore is the from the backend on whether the user has clicked
            // and saved the checkbox before.
            disabled={(!acceptedDisclaimerChecked && !acceptedDisclaimer) || !personalNote.trim() || transitioning}
            data-test="send-for-signatures"
            loading={isFetching || isMutating}
          >
            {shareContent.PRIMARY_BUTTON_LABEL}
          </Button>
        </ButtonGroup>
      ),
      [RequestSignatureModal.EmailPreviewContent]: (
        <Button tone="brand" emphasis="filled" onClick={onCloseEmailPreview}>
          OK
        </Button>
      ),
      [RequestSignatureModal.LeaseSentContent]: (
        <ButtonGroup aria-label="lease sent options">
          <TextButton tone="brand" onClick={goToDashboard} data-test="go-to-dashboard">
            Go to your dashboard
          </TextButton>
          {sentContent.PRIMARY_BUTTON_LABEL && (
            <DownloadButton
              buttonType="primary"
              url={lease.getPDFLink(sentContent.LEASE_DRAFT)}
              buttonLabel={sentContent.PRIMARY_BUTTON_LABEL}
            />
          )}
        </ButtonGroup>
      ),
    };

    return footerMapping[activeComponent] || null;
  }

  function renderBodyContent(): React.ReactNode {
    const componentMapping = {
      [RequestSignatureModal.ShareLeaseContent]: (
        <ShareLeaseContent
          key="share-lease"
          isInSignOnlineMode={isInSignOnlineMode}
          lease={lease}
          onOpenEmailPreview={onOpenEmailPreview}
          onSigningTabSelected={onSigningTabSelected}
          state={stateName}
          acceptedDisclaimerBefore={acceptedDisclaimer}
          acceptedAttachedLeaseBefore={acceptedAttachedLease}
          isSelfUpload={isSelfUpload}
          onPersonalNoteChange={onPersonalNoteChange}
          personalNote={personalNote}
          setPersonalNote={setPersonalNote}
          acceptedDisclaimerChecked={acceptedDisclaimerChecked}
          setAcceptedDisclaimerChecked={setAcceptedDisclaimerChecked}
          acceptedLeaseAttachmentChecked={acceptedLeaseAttachmentChecked}
          setAcceptedLeaseAttachmentChecked={setAcceptedLeaseAttachmentChecked}
        />
      ),
      [RequestSignatureModal.EmailPreviewContent]: (
        <EmailPreviewContent
          landlordProvidedDocuments={landlordProvidedDocuments}
          isInSignOnlineMode={isInSignOnlineMode}
          key="email"
          landlord={lease.landlords[0]}
          tenant={lease.tenants[0]}
          property={property}
          isSelfUpload={isSelfUpload}
          personalNote={personalNote}
        />
      ),
      [RequestSignatureModal.LeaseSentContent]: (
        <LeaseSentContent isInSignOnlineMode={isInSignOnlineMode} key="lease-sent" lease={lease} />
      ),
    };

    return componentMapping[activeComponent];
  }

  return (
    <LeaseModal
      header={
        <Heading level={1} css={{ textStyle: 'responsive.heading-lg' }}>
          {activeComponent === RequestSignatureModal.ShareLeaseContent ? 'Request signatures' : 'Signatures requested'}
        </Heading>
      }
      body={renderBodyContent()}
      onClose={onModalCloseWithTracking}
      isOpen
      footer={renderFooter()}
      shouldCloseOnOutsideClick={false}
    />
  );
}

RequestSignatureModal.ShareLeaseContent = 'ShareLeaseContent';
RequestSignatureModal.EmailPreviewContent = 'EmailPreviewContent';
RequestSignatureModal.LeaseSentContent = 'LeaseSentContent';

export default RequestSignatureModal;
